import type { ReactNode } from "react";
import { evidenceUrl, type AreaRow, type DecisionState, type EvidenceView, type Status } from "./lib/api";
import { DECISION, STATUS as S, STATUS_ORDER, fmtBytes, placeLine } from "./lib/status";

/** Small shared pieces. Nothing here owns state; everything here is presentational. */

export function StatusChip({ status }: { status: Status }) {
  const v = S[status];
  return (
    <span className={`status status-${status}`}>
      <span className="status-icon" aria-hidden>
        {v.icon}
      </span>
      {v.label}
    </span>
  );
}

export function DecisionChip({ state }: { state: DecisionState }) {
  const v = DECISION[state];
  return (
    <span className={`chip chip-${state}`}>
      <span aria-hidden>{v.icon}</span> {v.label}
    </span>
  );
}

/** The three-state legend. Shape and word, then colour - in that order of importance. */
export function Legend() {
  return (
    <div className="map-panel map-legend">
      {STATUS_ORDER.map((s) => (
        <div className="k" key={s}>
          <span className={`legend-swatch sw-${s}`} aria-hidden />
          <span className="dim">
            {S[s].icon} {S[s].label}
          </span>
          <span className="mute">({S[s].patternWord})</span>
        </div>
      ))}
    </div>
  );
}

export function ProgressBar({ a }: { a: Pick<AreaRow, "required_count" | "granted_count" | "refused_count"> }) {
  const r = Math.max(1, a.required_count);
  return (
    <div className="bar" title={`${a.granted_count} disetujui dari ${a.required_count}`}>
      <div className="bar-fill" style={{ width: `${(a.granted_count / r) * 100}%`, background: "var(--permitted)" }} />
      <div className="bar-fill" style={{ width: `${(a.refused_count / r) * 100}%`, background: "var(--blocked)" }} />
    </div>
  );
}

export function Spinner() {
  return <span className="spinner" aria-label="memuat" />;
}

/**
 * Page-level loading: viewport-centered, never container-centered. A spinner
 * hugging the top-left of its box reads as a broken layout; one in the
 * middle of the screen reads as loading. Transparent and click-through --
 * it reports a wait, it does not trap the thumb.
 */
export function ScreenLoading({ label = "Memuat…" }: { label?: string }) {
  return (
    <div className="screen-loading" role="status" aria-label={label}>
      <span className="spinner" />
    </div>
  );
}

export function Warn({ tone, children }: { tone: "amber" | "red" | "blue" | "green"; children: ReactNode }) {
  return <div className={`warn warn-${tone}`}>{children}</div>;
}

/**
 * One piece of evidence.
 *
 * A pending tile is not an error state: the decision is already recorded and the
 * photo is on its way. It says so, and says how many attempts have been made,
 * because a badge that silently spins forever is worse than one that admits the
 * upload is stuck.
 */
export function EvidenceTile({
  ev,
  canOpen,
  canDelete,
  onDelete,
}: {
  ev: EvidenceView;
  canOpen: boolean;
  canDelete: boolean;
  onDelete?: (ev: EvidenceView) => void;
}) {
  if (!ev.landed) {
    return (
      <div className="ev ev-pending" title={ev.last_error ?? "Menunggu unggahan"}>
        <span>
          {ev.last_error ? "Gagal" : "Menunggu"}
          <br />
          <span className="mono">{ev.upload_attempts}×</span>
        </span>
      </div>
    );
  }
  const isPhoto = (ev.content_type ?? "").startsWith("image/");
  return (
    <div className="ev">
      {canOpen ? (
        <a href={evidenceUrl(ev.id)} target="_blank" rel="noreferrer" title={`${ev.content_type} · ${fmtBytes(ev.byte_size)}`}>
          {isPhoto ? <img src={evidenceUrl(ev.id)} alt="Bukti" loading="lazy" /> : <span>PDF<br />dokumen</span>}
        </a>
      ) : (
        <span title="Akses bukti tidak tersedia" className="center">
          <span aria-hidden>🔒</span>
          <br />
          <span className="small">terkunci</span>
        </span>
      )}
      {canDelete && onDelete && (
        <button
          className="btn-ghost"
          style={{ position: "absolute", inset: "auto 0 0 0", borderRadius: 0, fontSize: 10, padding: "1px 0" }}
          onClick={() => onDelete(ev)}
          title="Hapus bukti"
        >
          hapus
        </button>
      )}
    </div>
  );
}

export { S as STATUS_VISUAL, placeLine };
export type { Status };
